// components/ui/popup.tsx
'use client'
import React, { useState, useRef, useEffect } from 'react'
import { gsap } from 'gsap'
import Image from 'next/image'
import Button from './button'
import { usePathname } from 'next/navigation'
import PopupProgressNav from '@/components/cdp/ProgressNav'

const Popup = () => {
    const [isPopupVisible, setIsPopupVisible] = useState(false)
    const [isIconVisible, setIsIconVisible] = useState(true)
    const [hasUserInteracted, setHasUserInteracted] = useState(false)
    const popupRef = useRef<HTMLDivElement>(null)
    const iconRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)
    const pathname = usePathname()
    const allowedPages = ['/careers', '/resources/customer-data-platform']
    const shouldShow = allowedPages.includes(pathname)
    const isCDPPage = pathname === '/resources/customer-data-platform'

    const minimizePopup = () => {
        if (!popupRef.current || !contentRef.current) return

        const tl = gsap.timeline({
            onComplete: () => {
                setIsPopupVisible(false)
                setIsIconVisible(true)
            }
        })

        tl.to(Array.from(contentRef.current.children), {
            opacity: 0,
            y: 20,
            duration: 0.6,
            stagger: 0.05,
            ease: "power2.inOut"
        })

        .to(popupRef.current, {
            scale: 0.1,
            x: 700,
            y: 350,
            borderRadius: "50%",
            duration: 0.3,
            ease: "power3.inOut"
        }, "-=0.3")
    }

    const maximizePopup = () => {
        setIsIconVisible(false)
        setIsPopupVisible(true)
        setHasUserInteracted(true)

        requestAnimationFrame(() => {
            if (!popupRef.current || !contentRef.current) return

            const tl = gsap.timeline()

            tl.fromTo(popupRef.current,
                { scale: 0.1, x: 700, y: 350, borderRadius: "50%", opacity: 1 },
                { scale: 1, x: 0, y: 0, borderRadius: "0.75rem", duration: 0.5, ease: "power3.out" }
            )
            .fromTo(Array.from(contentRef.current.children),
                { opacity: 0, y: -20 },
                { opacity: 1, y: 0, duration: 0.4, stagger: 0.08, ease: "power2.out" },
                "-=0.3"
            )
        })
    }

    useEffect(() => {
        if (!shouldShow) return

        const timer = setTimeout(() => {
            if (!hasUserInteracted) {
                maximizePopup()
            }
        }, 3000)

        return () => clearTimeout(timer)
    }, [shouldShow, hasUserInteracted])

    useEffect(() => {
        if (!shouldShow) return

        const dnaSection = document.querySelector('#dna-section')
        if (!dnaSection) return

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && isPopupVisible && hasUserInteracted) {
                    setTimeout(() => minimizePopup(), 500)
                }
            },
            { threshold: 0.05 }
        )

        observer.observe(dnaSection)
        return () => observer.disconnect()
    }, [shouldShow, isPopupVisible, hasUserInteracted])

    if (!shouldShow) return null

    return (
        <>
            <div
                ref={iconRef}
                className={`fixed bottom-8 right-8 w-12 h-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-full flex items-center justify-center cursor-pointer shadow-lg hover:scale-110 transition-transform duration-200 z-50 ${isIconVisible ? 'block' : 'hidden'}`}
                onClick={maximizePopup}
            >
                <Image
                    src={isCDPPage ? "/chapter-icon.png" : "/floating-icon.webp"}
                    alt="Open popup"
                    width={24}
                    height={24}
                    className="object-contain"
                />
            </div>

            {isPopupVisible && (
                <div className="pointer-events-none fixed right-8 left-8 bottom-8 md:right-12 md:bottom-12 flex items-center justify-center md:justify-end z-50">
                    <div
                        ref={popupRef}
                        className={`rounded-xl p-10 shadow-2xl max-w-sm w-full text-center relative pointer-events-auto backdrop-blur-xl ${isCDPPage
                            ? 'bg-[#410080] border border-white/20 text-white'
                            : 'bg-white/95 border border-white/20'
                        }`}
                    >
                        <button
                            onClick={minimizePopup}
                            className="absolute top-4 right-6 text-2xl text-gray-600 hover:text-gray-800"
                        >
                            ×
                        </button>

                        <div ref={contentRef}>
                            {isCDPPage ? (
                                <PopupProgressNav />
                            ) : (
                                <>
                                    <h3 className="text-lg font-semibold">Announcement</h3>
                                    <p className="mt-2">Please Be Aware Of Job Scams</p>
                                    <Button text="Read more" link="/beware-of-job-scams" className="pointer-events-auto"/>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default Popup